/**
 * Compresses an image file client-side using the Canvas API to reduce resolution and quality.
 * Useful to keep uploads well under target size limits (e.g. < 500KB).
 *
 * @param {File} file - The original image File to compress.
 * @param {number} maxDimension - Maximum width or height in pixels (default: 1200).
 * @param {number} quality - JPEG output quality from 0.0 to 1.0 (default: 0.7).
 * @returns {Promise<File>} A promise resolving to the compressed File, or the original if compression is not applicable or fails.
 */
export const compressImage = (file, maxDimension = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
        if (!file || !file.type.startsWith('image/')) {
            resolve(file);
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > maxDimension || height > maxDimension) {
                    if (width > height) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    } else {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const compressedFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        });
                        resolve(compressedFile);
                    } else {
                        resolve(file);
                    }
                }, 'image/jpeg', quality);
            };
            img.onerror = () => resolve(file);
        };
        reader.onerror = () => resolve(file);
    });
};

/**
 * Automatically crops an image file to the maximum possible centered square.
 *
 * @param {File} file - The original image File to crop.
 * @param {number} maxDimension - Maximum dimension of the square in pixels (default: 1000).
 * @param {number} quality - Output quality from 0.0 to 1.0 (default: 0.9).
 * @returns {Promise<File>} A promise resolving to the cropped square File.
 */
export const cropToCenterSquare = (file, maxDimension = 1000, quality = 0.9) => {
    return new Promise((resolve) => {
        if (!file || !file.type.startsWith('image/')) {
            resolve(file);
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const width = img.naturalWidth || img.width;
                const height = img.naturalHeight || img.height;

                // Determine the maximum possible square that fits within the image
                const side = Math.min(width, height);
                // Center the square box within the source image
                const startX = Math.round((width - side) / 2);
                const startY = Math.round((height - side) / 2);

                // Output square dimension (capped at maxDimension if source is larger)
                const outputSide = Math.min(side, maxDimension);

                const canvas = document.createElement('canvas');
                canvas.width = outputSide;
                canvas.height = outputSide;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(file);
                    return;
                }

                const isPng = file.type === 'image/png';
                if (!isPng) {
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, outputSide, outputSide);
                }

                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Draw the centered square slice from the image onto the square canvas
                ctx.drawImage(img, startX, startY, side, side, 0, 0, outputSide, outputSide);

                const outputMime = isPng ? 'image/png' : 'image/jpeg';
                canvas.toBlob((blob) => {
                    if (blob) {
                        const croppedFile = new File([blob], file.name, {
                            type: outputMime,
                            lastModified: Date.now()
                        });
                        resolve(croppedFile);
                    } else {
                        resolve(file);
                    }
                }, outputMime, isPng ? undefined : quality);
            };
            img.onerror = () => resolve(file);
        };
        reader.onerror = () => resolve(file);
    });
};
